import React, { useState } from 'react'
import { t, Trans } from '@lingui/macro'

import { Modal } from '../../../components/common/modal/Modal'

import { ButtonPrimary, ButtonClean } from 'front-end/src/components'
import Input from '../../../components/common/form/input/Input'
import Form, { FormRow } from '../../../components/common/form/Form'
import Label from '../../../components/common/form/label/Label'
import uniqueId from '../../../components/utils/uniqueId'
import HorizontalChoiceSelector from '../../../components/common/horizontal-choice-selector/HorizontalChoiceSelector'
import { CampaignScheduleEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'

import './CreateCampaignModal.less'
import {
  FixedCampaignIcon,
  ImmediateCampaignIcon,
  RelativeCampaignIcon
} from '../../manage-campaign-page/utils'
import { useCreateCampaignMutation } from './CreateCampaignModal.codegen'

interface IProps {
  onHide: () => void
  onCreate: (id: number) => void
}

function RelativeCampaignLogo() {
  return (
    <div className="Campaign__icon">
      <RelativeCampaignIcon />
    </div>
  )
}

function FixedCampaignLogo() {
  return (
    <div className="Campaign__icon">
      <FixedCampaignIcon />
    </div>
  )
}

function ImmediateCampaignLogo() {
  return (
    <div className="Campaign__icon">
      <ImmediateCampaignIcon />
    </div>
  )
}

export default (props: IProps) => {
  const { onHide, onCreate } = props
  const [name, setName] = useState<string>('')
  const [scheduleType, setScheduleType] = useState<CampaignScheduleEnum | null>(
    null
  )
  const [isCreating, setIsCreating] = useState<boolean>(false)
  const inputTitleId = uniqueId()

  const options = [
    {
      value: CampaignScheduleEnum.RELATIVE,
      description: (
        <>
          <RelativeCampaignLogo />
          <div className="description">
            <h3>
              <Trans>Relative timing</Trans>
            </h3>
            <span>
              <Trans>
                Actions take place one after another, starting whenever a user
                joins the audience
              </Trans>
            </span>
          </div>
        </>
      )
    },
    {
      value: CampaignScheduleEnum.FIXED,
      description: (
        <>
          <FixedCampaignLogo />
          <div className="description">
            <h3>
              <Trans>Fixed timing</Trans>
            </h3>
            <span>
              <Trans>
                Actions take place at a fixed time and date, for all users in
                the audience
              </Trans>
            </span>
          </div>
        </>
      )
    },
    {
      value: CampaignScheduleEnum.IMMEDIATE,
      description: (
        <>
          <ImmediateCampaignLogo />
          <div className="description">
            <h3>
              <Trans>Immediate timing</Trans>
            </h3>
            <span>
              <Trans>
                Actions take place immediately after activating campaign. No
                time setup.
              </Trans>
            </span>
          </div>
        </>
      )
    }
  ]

  const [createCampaign] = useCreateCampaignMutation()

  const create = async () => {
    setIsCreating(true)
    try {
      const { data } = await createCampaign({
        variables: {
          name: name,
          schedule_type: scheduleType as CampaignScheduleEnum
        }
      })
      if (data) {
        if (data.admin?.createCampaign?.name) {
          setName(data.admin?.createCampaign?.name)
        }
        onCreate(data.admin?.createCampaign?.id as number)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Modal
      className="CreateCampaignModal"
      header={t`Create a new campaign`}
      onHide={onHide}
      controls={[
        <ButtonClean key={1} onClick={onHide}>
          <Trans>Close</Trans>
        </ButtonClean>,
        <ButtonPrimary
          disabled={!name || !scheduleType}
          key={2}
          onClick={create}
          isLoading={isCreating}
        >
          <Trans>Create</Trans>
        </ButtonPrimary>
      ]}
    >
      <Form>
        <FormRow>
          <Label htmlFor={inputTitleId}>
            <Trans>Campaign name</Trans>
          </Label>
          <Input
            autoFocus
            value={name}
            onChange={(val) => {
              setName(val)
            }}
            id={inputTitleId}
            fullWidth
          />
          <span className="CreateCampaignModal__input-note">
            <Trans>Note: This will not be visible to your users</Trans>
          </span>
        </FormRow>
        <FormRow>
          <h3 className="CreateCampaignModal__subhead">
            <Trans>Select a scheduling type</Trans>:
          </h3>
          <span className="CreateCampaignModal__input-note">
            <Trans>
              you can always change these settings later if you need to
            </Trans>
          </span>
          <HorizontalChoiceSelector
            name="type"
            onChange={(val) => {
              setScheduleType(val as CampaignScheduleEnum)
            }}
            options={options}
            value={scheduleType as CampaignScheduleEnum}
          />
        </FormRow>
      </Form>
    </Modal>
  )
}
